export * from "ce/selectors/packageSelectors";
import type { DefaultRootState } from "react-redux";

import { createSelector } from "reselect";
import type { Module } from "ee/constants/ModuleConstants";
import type { Package } from "ee/constants/PackageConstants";
import {
  ENTITY_EXPLORER_RENDER_ORDER,
  MODULE_TYPE,
  PACKAGE_PULL_STATUS,
} from "ee/constants/ModuleConstants";

export const getIsFetchingPackages = (state: DefaultRootState) =>
  state.ui.workspaces.loadingStates.isFetchingPackagesList;

export const getIsCreatingPackage = (
  state: DefaultRootState,
  workspaceId: string,
) => state.ui.workspaces.loadingStates.packageCreationRequestMap[workspaceId];

export const getPackagesList = (state: DefaultRootState) =>
  state.ui.workspaces.packagesList;

export const getCurrentPackageId = (state: DefaultRootState) =>
  state.ui.editor?.currentPackageId;

export const getCurrentBasePackageId = (state: DefaultRootState) =>
  state.ui.editor?.currentBasePackageId || "";

export const getPackages = (state: DefaultRootState) => state.entities.packages;

export const getCurrentPackage = createSelector(
  getCurrentPackageId,
  getPackages,
  (currentPackageId, packages) =>
    currentPackageId ? packages[currentPackageId] : null,
);

export const getIsPackageEditorInitialized = (state: DefaultRootState) =>
  state.ui.editor.isPackageEditorInitialized;

export const getIsSavingPackageName = (state: DefaultRootState) =>
  state.ui.workspaces.isSavingPkgName;

export const getisErrorSavingPackageName = (state: DefaultRootState) =>
  state.ui.workspaces.isErrorSavingPkgName;

export const getIsPackagePublishing = (state: DefaultRootState) =>
  state.ui.editor.isPackagePublishing;

export const getFirstModule = (state: DefaultRootState) => {
  const modules = Object.values(state.entities.modules);
  const firstModuleOfEachType: Record<MODULE_TYPE, Module | undefined> = {
    [MODULE_TYPE.UI]: undefined,
    [MODULE_TYPE.QUERY]: undefined,
    [MODULE_TYPE.JS]: undefined,
  };

  modules.forEach((module) => {
    if (!firstModuleOfEachType[module.type]) {
      firstModuleOfEachType[module.type] = module;
    }
  });

  return ENTITY_EXPLORER_RENDER_ORDER.reduce(
    (acc: Module | undefined, next) => {
      acc = acc || firstModuleOfEachType[next];

      return acc;
    },
    undefined,
  );
};

export const getModulesMetadata = (state: DefaultRootState) =>
  state.ui.explorer.modulesMetadata;

export const getModulesMetadataById = (state: DefaultRootState, id: string) =>
  state.ui.explorer.modulesMetadata[id];

export const getPackageById = (
  state: DefaultRootState,
  id: string,
): Package | undefined => state.entities.packages[id];

export const getPackageByBaseId = createSelector(
  getPackages,
  (_: DefaultRootState, basePackageId: string) => basePackageId,
  (packages, basePackageId) => {
    return Object.values(packages).find((pkg) => pkg.baseId === basePackageId);
  },
);

export const getPackagePullStatus = (state: DefaultRootState) =>
  state.ui.editor.packagePullStatus;

export const getIsPackageUpgrading = (state: DefaultRootState) =>
  state.ui.editor.packagePullStatus === PACKAGE_PULL_STATUS.UPGRADING;

export const selectChangeConsumedPackageVersionLoading = (
  state: DefaultRootState,
) => state.ui.editor.changeConsumedPackageVersionLoading;

export const getHasPrivateEntityForPackage = createSelector(
  getCurrentPackage,
  (currentPackage) => currentPackage?.hasPrivateEntity ?? true,
);
